import React, { useState, useEffect } from "react";
import {
  Grid,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Menu,
  MenuItem,
  Tooltip
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import DeleteIcon from "@mui/icons-material/Delete";
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";

import IconSelect from "./Icon-select";
import MDButton from "components/MDButton";
import MDEditor from "@uiw/react-md-editor";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import PdfPreviewModal from "./PdfPreviewModal";
import VideoPreviewModal from "./VideoPreviewModal";
import { SwapVert } from "@mui/icons-material";

const ModuleResources = ({ module, moduleIndex, modules, setModules }) => {
  const [pdfModalUrl, setPdfModalUrl] = useState("");
  const [videoModalUrl, setVideoModalUrl] = useState("");
  const [addMenuAnchor, setAddMenuAnchor] = useState(null);
  const [dragMode, setDragMode] = useState(false);
  const [expandedIndices, setExpandedIndices] = useState([]);
  const [localResourceNames, setLocalResourceNames] = useState(
    module.resources.map((r) => r.name || "")
  );
  const [showHeaderEditor, setShowHeaderEditor] = useState([]);
  const [showFooterEditor, setShowFooterEditor] = useState([]);


  const handleDragEnd = (result) => {
    if (!result.destination) return;
    const updatedModules = [...modules];
    const reordered = [...updatedModules[moduleIndex].resources];

    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);

    updatedModules[moduleIndex].resources = reordered;
    setModules(updatedModules);
  };

  useEffect(() => {
    setLocalResourceNames(module.resources.map((r) => r.name || ""));
  }, [module.resources]);

  useEffect(() => {
    setExpandedIndices([]);
  }, [module, module.resources]);

  useEffect(() => {
    setShowHeaderEditor([]);
    setShowFooterEditor([]);
  }, [module.resources]);

  return (
    <Grid container spacing={2} mt={2}>
      <MDBox width="100%">
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId={`droppable-${moduleIndex}`}>
            {(provided) => (
              <div ref={provided.innerRef} {...provided.droppableProps}>
                {module.resources.map((resource, resourceIndex) => (
                  <Draggable
                    key={`resource-${resourceIndex}`}
                    draggableId={`resource-${moduleIndex}-${resourceIndex}`}
                    index={resourceIndex}
                    isDragDisabled={!dragMode}
                  >
                    {(provided) => (
                      <Grid
                        item
                        xs={12}
                        sx={{ pl: 2, pb: 2 }}
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                      >
                        <Accordion
                          sx={{ border: "1px solid #e0e0e0" }}
                          expanded={expandedIndices.includes(resourceIndex)}
                          onChange={() => {
                            setExpandedIndices((prev) =>
                              prev.includes(resourceIndex)
                                ? prev.filter((i) => i !== resourceIndex)
                                : [...prev, resourceIndex]
                            );
                          }}
                        >
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <MDBox display="flex" alignItems="center">
                              {dragMode && (
                                <DragIndicatorIcon
                                  fontSize="small"
                                  sx={{ cursor: "grab", marginRight: 1, pointerEvents: "none" }}
                                  {...provided.dragHandleProps}
                                />
                              )}
                              <MDTypography variant="h5">
                                Resource {resourceIndex + 1}: {resource.name || "Untitled"}
                              </MDTypography>
                            </MDBox>
                          </AccordionSummary>
                          <AccordionDetails>
                            <Grid container spacing={2}>
                              <Grid item xs={12}>
                                <Grid container alignItems="center" spacing={1}>
                                  <Grid item sx={{ minWidth: "70px", maxWidth: "70px" }}>
                                    <IconSelect
                                      value={resource.icon}
                                      onChange={(newValue) => {
                                        const updatedModules = [...modules];
                                        updatedModules[moduleIndex].resources[resourceIndex].icon = newValue;
                                        setModules(updatedModules);
                                      }}
                                    />
                                  </Grid>

                                  <Grid item sx={{ flex: 1 }}>
                                    <TextField
                                      fullWidth
                                      label="Resource Name"
                                      name="name"
                                      value={localResourceNames[resourceIndex] || ""}
                                      onChange={(e) => {
                                        const updatedNames = [...localResourceNames];
                                        updatedNames[resourceIndex] = e.target.value;
                                        setLocalResourceNames(updatedNames);
                                      }}
                                      onBlur={() => {
                                        const updatedModules = [...modules];
                                        updatedModules[moduleIndex].resources[resourceIndex].name = localResourceNames[resourceIndex];
                                        setModules(updatedModules);
                                      }}
                                    />
                                  </Grid>
                                </Grid>
                              </Grid>

                              <Grid item xs={12}>
                                {resource.description || showHeaderEditor.includes(resourceIndex) ? (
                                  <>
                                    <MDBox display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                                      <MDTypography variant="body2" >
                                        Header:
                                      </MDTypography>
                                      <Tooltip title="Content shown before the main content" arrow>
                                        <HelpOutlineIcon
                                          fontSize="small"
                                          sx={{ color: "text.secondary", cursor: "help" }}
                                        />
                                      </Tooltip>
                                    </MDBox>
                                    <MDBox mx={0.1}>
                                      <div data-color-mode="light">
                                        <MDEditor
                                          name="description"
                                          value={resource.description}
                                          onChange={(value) => {
                                            const updatedModules = [...modules];
                                            updatedModules[moduleIndex].resources[resourceIndex].description = value;
                                            setModules(updatedModules);
                                          }}
                                        />
                                        <MDButton
                                          variant="text"
                                          color="error"
                                          sx={{ paddingLeft: 0 }}
                                          onClick={() =>
                                            setShowHeaderEditor((prev) => prev.filter((i) => i !== resourceIndex))
                                          }
                                        >
                                          - Remove Header
                                        </MDButton>
                                      </div>
                                    </MDBox>
                                  </>
                                ) : (
                                  <MDButton
                                    variant="text"
                                    color="info"
                                    sx={{ paddingLeft: 0 }}
                                    onClick={() =>
                                      setShowHeaderEditor((prev) => [...prev, resourceIndex])
                                    }
                                  >
                                    + Add Header
                                  </MDButton>
                                )}
                              </Grid>

                              {resource.type === "MARKDOWN" && (
                                <Grid item xs={12}>
                                  <MDBox display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                                    <MDTypography variant="body2" >Markdown:</MDTypography>
                                    <Tooltip title="This is the main content" arrow>
                                      <HelpOutlineIcon
                                        fontSize="small"
                                        sx={{ color: "text.secondary", cursor: "help" }}
                                      />
                                    </Tooltip>
                                  </MDBox>
                                  <MDBox mx={0.1}>
                                    <div data-color-mode="light">
                                      <MDEditor
                                        name="markdown"
                                        value={resource.markdown}
                                        onChange={(value) => {
                                          const updatedModules = [...modules];
                                          updatedModules[moduleIndex].resources[resourceIndex].markdown = value;
                                          setModules(updatedModules);
                                        }}
                                      />
                                    </div>
                                  </MDBox>
                                </Grid>
                              )}

                              {resource.type === "SLIDE" && (
                                <Grid item xs={12} display="flex" justifyContent="space-between">
                                  <TextField
                                    fullWidth
                                    label="Module PDF URL"
                                    name="module_slide_link"
                                    type="link"
                                    helperText="Provide a public URL for the PDF"
                                    value={resource.url}
                                    onChange={(e) => {
                                      const updatedModules = [...modules];
                                      updatedModules[moduleIndex].resources[resourceIndex].url = e.target.value;
                                      setModules(updatedModules);
                                    }}
                                  />
                                  <MDButton
                                    variant="contained"
                                    style={{ marginLeft: "10px", marginBottom: "18px" }}
                                    onClick={() => setPdfModalUrl(resource.url)}
                                  >
                                    Preview
                                  </MDButton>
                                </Grid>
                              )}

                              {resource.type === "VIDEO" && (
                                <Grid item xs={12} display="flex" justifyContent="space-between">
                                  <TextField
                                    fullWidth
                                    label="Module Video URL"
                                    name="module_video_link"
                                    type="link"
                                    helperText="Provide a public video URL"
                                    value={resource.url}
                                    onChange={(e) => {
                                      const updatedModules = [...modules];
                                      updatedModules[moduleIndex].resources[resourceIndex].url = e.target.value;
                                      setModules(updatedModules);
                                    }}
                                  />
                                  <MDButton
                                    variant="contained"
                                    style={{ marginLeft: "10px", marginBottom: "18px" }}
                                    onClick={() => setVideoModalUrl(resource.url)}
                                  >
                                    Preview
                                  </MDButton>
                                </Grid>
                              )}

                              <Grid item xs={12}>
                                {resource.followup || showFooterEditor.includes(resourceIndex) ? (
                                  <>
                                    <MDBox display="flex" alignItems="center" justifyContent="space-between" mb={1}>

                                      <MDTypography variant="body2" >
                                        Footer:
                                      </MDTypography>
                                      <Tooltip title="Content shown after the main content">
                                        <HelpOutlineIcon
                                          fontSize="small"
                                          sx={{ color: "text.secondary", cursor: "help" }}
                                        />
                                      </Tooltip>
                                    </MDBox>
                                    <MDBox mx={0.1}>
                                      <div data-color-mode="light">
                                        <MDEditor
                                          name="followup"
                                          value={resource.followup}
                                          onChange={(value) => {
                                            const updatedModules = [...modules];
                                            updatedModules[moduleIndex].resources[resourceIndex].followup = value;
                                            setModules(updatedModules);
                                          }}
                                        />
                                        <MDButton
                                          variant="text"
                                          color="error"
                                          sx={{ paddingLeft: 0 }}
                                          onClick={() =>
                                            setShowFooterEditor((prev) => prev.filter((i) => i !== resourceIndex))
                                          }
                                        >
                                          - Remove Footer
                                        </MDButton>
                                      </div>
                                    </MDBox>
                                  </>
                                ) : (
                                  <MDButton
                                    variant="text"
                                    color="info"
                                    sx={{ paddingLeft: 0 }}
                                    onClick={() =>
                                      setShowFooterEditor((prev) => [...prev, resourceIndex])
                                    }
                                  >
                                    + Add Footer
                                  </MDButton>
                                )}
                              </Grid>

                              <Grid item xs={12} display="flex" justifyContent="flex-end">
                                <MDButton
                                  variant="gradient"
                                  color="error"
                                  startIcon={<DeleteIcon />}
                                  onClick={() => {
                                    const updatedModules = [...modules];
                                    updatedModules[moduleIndex].resources.splice(resourceIndex, 1);
                                    setModules(updatedModules);
                                  }}
                                >
                                  Remove Resource
                                </MDButton>
                              </Grid>
                            </Grid>
                          </AccordionDetails>
                        </Accordion>
                      </Grid>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </MDBox>

      <PdfPreviewModal open={!!pdfModalUrl} onClose={() => setPdfModalUrl("")} url={pdfModalUrl} />
      <VideoPreviewModal open={!!videoModalUrl} onClose={() => setVideoModalUrl("")} url={videoModalUrl} />

      <Grid item xs={12} display="flex" justifyContent="flex-end" mt={3}>
        <MDButton
          startIcon={<SwapVert />}
          variant="gradient"
          color={dragMode ? "warning" : "secondary"}
          style={{ marginRight: "10px" }}
          onClick={() => {
            setExpandedIndices([]);
            setDragMode(!dragMode);
          }}
        >
          {dragMode ? "Finish Rearranging" : "Rearrange Resource"}
        </MDButton>
        <MDButton
          variant="gradient"
          color="primary"
          style={{ marginRight: "10px" }}
          startIcon={<UnfoldMoreIcon />}
          onClick={() => setExpandedIndices([])}
        >
          Collapse All
        </MDButton>


        <MDButton
          variant="gradient"
          color="info"
          onClick={(event) => setAddMenuAnchor(event.currentTarget)}
          endIcon={<ArrowDropDownIcon />}
        >
          Add Resource
        </MDButton>

        <Menu
          anchorEl={addMenuAnchor}
          open={Boolean(addMenuAnchor)}
          onClose={() => setAddMenuAnchor(null)}
        >
          {["MARKDOWN", "SLIDE", "VIDEO"].map((type) => (
            <MenuItem
              key={type}
              onClick={() => {
                const updatedModules = [...modules];
                updatedModules[moduleIndex].resources.push({
                  type,
                  icon: "",
                  name: "",
                  description: "",
                  markdown: "",
                  url: "",
                  followup: "",
                });
                setModules(updatedModules);
                setAddMenuAnchor(null);
              }}
            >
              {type}
            </MenuItem>
          ))}
        </Menu>
      </Grid>
    </Grid>
  );
};

export default ModuleResources;